import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import AppConfig from '@config';
import { getStorageItem, getToken, STORAGE_ACTION } from '@utils/storage';
import { tokenRefresh } from '../apollo/utils';
import jwtDecode, { JwtPayload } from 'jwt-decode';
import * as Sentry from '@sentry/react';
import { CapaServiceType } from '@generated/graphql';

const fileApiUrl = window.location.host.includes('bluegreen')
  ? `https://capa-api-bluegreen.capa.ai`
  : AppConfig.fileApiUrl;

const instance = axios.create({
  baseURL: fileApiUrl,
  headers: {
    Accept: '*/*',
    'Content-type': 'multipart/form-data'
  }
});

instance.interceptors.request.use(
  (config: AxiosRequestConfig) => {
    const token = getToken();
    if (token) {
      return {
        ...config,
        headers: {
          ...config.headers,
          Authorization: `Bearer ${getToken()}`
        }
      };
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

instance.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error: AxiosError) => {
    if (error.response?.status !== 401) {
      //  Sentry
      const accessToken = getStorageItem(STORAGE_ACTION.CLIENT_ACCESS_TOKEN);
      const decodedJwt = accessToken ? jwtDecode<JwtPayload>(accessToken) : {};
      const userEmail = decodedJwt.aud ? decodedJwt.aud.toString() : undefined;

      if (accessToken) {
        const userinfo = {
          email: userEmail,
          decodedJwt: decodedJwt
        };
        Sentry.setTag('Email', userEmail);
        Sentry.setUser(userinfo);
      }

      Sentry.setTag('Service', CapaServiceType.Quotation);
      Sentry.setTag('Api', 'fileAPI');
      Sentry.withScope((scope) => {
        scope.setLevel(Sentry.Severity.Error);
        scope.setContext('response', { ...error.response });

        Sentry.captureMessage(`file`);
        scope.clear();
      });
    }

    if (error.response?.status === 401) {
      tokenRefresh();
    }

    return Promise.reject(error);
  }
);

export const CapaFileUpload = async (
  req: { formData: FormData },
  config?: AxiosRequestConfig
) => {
  return await instance.post('/file', req.formData, config);
};

export async function fileDownloadApi(
  fileId: string,
  config?: AxiosRequestConfig
) {
  return await instance.get(`/file/${fileId}`, {
    responseType: 'blob',
    ...config
  });
}

export async function pdfDownloadApi(fileId: string, config?: any) {
  return await instance.get(`${fileId}`, {
    responseType: 'blob'
  });
}

export const CapaConnectFileUpload = async (url: string, req: FormData) => {
  return await instance.post(
    AppConfig.mode === 'development'
      ? `https://capa-api-staging.capa.ai${url}`
      : url,
    req
  );
};

const fetcher = async (url: string) => {
  return await instance.get(url);
};

const wordPressBlogFetcher = async (url: string) => {
  return await instance.get(`${AppConfig.wordPressUrl}${url}`);
};

const productionWordpressBlogFetcher = async (url: string) => {
  return await instance.get(`https://capa-blog-api.capa.ai/wp-json${url}`);
};

export {
  instance,
  wordPressBlogFetcher,
  productionWordpressBlogFetcher,
  fetcher
};
